/**
 * IL CATALOGO DEI MODULI PER AZIENDA — la parte pura, senza rete.
 *
 * Sta in `shared/lib` e non in `features/tenant-modules` perche' la legge anche il catalogo errori
 * (`shared/api/jsonapi/errors.ts`) per dare un nome al modulo in un 403 `MODULE_DISABLED`, e
 * `shared` non importa da `features`. La parte che parla col BE sta in `features/tenant-modules/api.ts`.
 *
 * Contratto: `BE-CONTRACT-TENANT-MODULES-v1` (repo BE, 11-09-2026), §2 per il catalogo.
 */

/**
 * I sedici codici del catalogo BE (`ModuleCatalog.All`), nell'ordine del catalogo.
 *
 * `foodcost` e' entrato il 26-10-2026 (`BE-RESPONSE-TO-FE-MODULI-v1` §4): fino ad allora era un flag
 * fuori catalogo, letto solo da `tenant-attributes`, e quindi l'unico che un'azienda si poteva
 * accendere da sola. Ora vale anche per lui «lo accende la piattaforma».
 *
 * `tasks`, `workflows` e `planning` dal 02-11-2026 (BE, ramo «modulo tasks spegnibile»): sono i
 * moduli **di base**, accesi alla nascita, e `workflows` dipende da `tasks` (`requires` nella
 * lettura dei moduli). Un BE che non li conosce non li nomina in `me.modules`, e valgono accesi.
 */
export const MODULE_CODES = [
  'reporting', 'smartWorking', 'attendance', 'travel', 'fleet',
  'meetingRoom', 'serviceReport', 'subscriptions', 'support', 'leave',
  'tasks', 'workflows', 'planning', 'maespace', 'mcp', 'foodcost',
] as const;

/** I moduli di base: alla creazione di un'azienda nascono accesi se non si dice altro (BE 3586 §5.3). */
export const BASE_MODULES: ReadonlySet<ModuleCode> = new Set<ModuleCode>(['tasks', 'workflows', 'planning']);

export type ModuleCode = (typeof MODULE_CODES)[number];

/**
 * I moduli **spenti senza riga** (`OffWhenMissing` sul BE): un collegamento a un servizio esterno
 * (`maespace`, `mcp`) o una funzione su misura (`foodcost`) non si accende da solo perche' a
 * un'azienda manca una riga. Gli altri, senza riga, valgono accesi.
 *
 * Serve solo al ripiego: `me.modules` porta gia' il valore effettivo. Ma finche' `/me` non ha
 * risposto, o con un BE che il codice non lo manda, il predefinito di chi chiama («acceso», per il
 * menu) li accenderebbe — e per questi tre e' la risposta sbagliata.
 */
export const OFF_WHEN_MISSING: ReadonlySet<ModuleCode> = new Set<ModuleCode>(['maespace', 'mcp', 'foodcost']);

/** La chiave di `tenant-attributes` di un modulo: e' sempre `<codice>.enabled` (il BE lo verifica all'avvio). */
export type ModuleFlag = `${ModuleCode}.enabled`;

/**
 * Nome breve del modulo, per le frasi che il BE non scrive: la schermata di diniego del `RouteGate`
 * e il messaggio del 403 quando il `detail` manca. Il testo lungo (`label`/`description`) resta
 * quello del catalogo BE, letto da `/tenant-modules`, cosi' la pagina «Moduli» non duplica niente.
 */
export const MODULE_NAME: Record<ModuleCode, string> = {
  reporting: 'Rendicontazione',
  smartWorking: 'Smart working',
  attendance: 'Presenze',
  travel: 'Trasferte',
  fleet: 'Flotta',
  meetingRoom: 'Sale riunioni',
  serviceReport: 'Rapportini di intervento',
  subscriptions: 'Abbonamenti',
  support: 'Assistenza',
  leave: 'Assenze',
  tasks: 'Task',
  workflows: 'Flussi di lavorazione',
  planning: 'Pianificazione',
  maespace: 'MaeSpace',
  mcp: 'Assistenti AI (MCP)',
  foodcost: 'Controllo di gestione',
};

export function isModuleCode(v: unknown): v is ModuleCode {
  return typeof v === 'string' && (MODULE_CODES as readonly string[]).includes(v);
}

/** `'fleet.enabled'` → `'fleet'`; `null` se non e' la chiave di un modulo del catalogo. */
export function moduleFromFlag(flag: string): ModuleCode | null {
  if (!flag.endsWith('.enabled')) return null;
  const code = flag.slice(0, -'.enabled'.length);
  return isModuleCode(code) ? code : null;
}

/** Nome del modulo per un codice o una chiave; il codice grezzo se non e' a catalogo. */
export function moduleName(codeOrFlag: string): string {
  const codice = moduleFromFlag(codeOrFlag) ?? (isModuleCode(codeOrFlag) ? codeOrFlag : null);
  return codice ? MODULE_NAME[codice] : codeOrFlag;
}

/**
 * Lo stato di un flag, con la precedenza giusta fra le sorgenti.
 *
 * `modules` e' il dizionario di `/account/me` (o `undefined` finche' non e' arrivato). Se dice
 * qualcosa su quel modulo, vince: e' la stessa riga che il cancello legge, ed e' leggibile da tutti.
 * Altrimenti si ricade su `tenant-attributes` (`ripiego`), che e' la strada di prima — vale per la
 * finestra in cui `/me` non ha risposto, e per un BE che quel modulo non lo nomina ancora. Per i
 * moduli spenti senza riga (`OFF_WHEN_MISSING`) il predefinito del ripiego e' sempre `false`.
 */
export function flagAcceso(
  modules: Record<string, boolean> | undefined,
  flag: string,
  fallback: (flag: string, defaultValue: boolean) => boolean,
  predefinito: boolean,
): boolean {
  const codice = moduleFromFlag(flag);
  if (codice && modules && typeof modules[codice] === 'boolean') return modules[codice];
  return fallback(flag, codice && OFF_WHEN_MISSING.has(codice) ? false : predefinito);
}
